import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getAllAdminNews, getAllArticles, getMethodNames, getNotification, updateNotification } from "services";

import { incomplete, useCancelAsyncOnUnmount, useNavigate, useTranslation } from "utils";

import { AdminHead, Button, Col, ErrorBoundary, Form, MainPage, Row, Message as message } from "components";

import { NotificationContent } from "pages/admin/users/components/NotificationContent";
import { NotificationSettings } from "pages/admin/users/components/NotificationSettings";
import { NotificationUserSegment } from "pages/admin/users/components/NotificationUserSegment";

export const NotificationUpdate = ({ id }) => {
  const { t } = useTranslation("enums");

  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [content, setContent] = useState("");
  const [type, setType] = useState("article");
  const [article, setArticle] = useState(null);
  const [sendDate, setSendDate] = useState(moment());
  const [notification, setNotification] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const loadAllNews = useCallback(() => {
    getAllAdminNews({
      signal,
    })
      .then((res) => {
        setNews(res.length > 0 ? res : []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  const loadAllArticles = useCallback(() => {
    getAllArticles({
      signal,
    }).then((res) => {
      setArticles(res.length > 0 ? res : []);
      loadAllNews();
    });
  }, [signal, loadAllNews]);

  const loadMethods = useCallback(() => {
    getMethodNames({
      signal,
    }).then((res) => {
      setMethods(res);
    });
  }, [signal]);

  const loadNotification = useCallback(() => {
    getNotification({
      id: id,
      signal,
    })
      .then((res) => {
        setNotification(res);
        setType(res.type);
        setSendDate(moment(res.sendDate, "DD.MM.YYYY HH:00"));
        setContent(res.description);
        setArticle(res.article);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, signal]);

  const complete = (v) => {
    updateNotification({
      ...v,
      ...{
        content: content,
        sendDate: sendDate,
        article: article,
        id: id,
        signal,
      },
    })
      .then(() => {
        navigate("/users/notifications");
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadNotification();
    loadMethods();
    loadAllArticles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline="Update a notification"
        link="/users/notifications"
        tooltipText="Back to notifications"
        description={<>Update a scheduled in-app and/or push notification. A notification will only be sent to users who have enabled them.</>}
      />
      <Row className="midi-form">
        <Col>
          <Form name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
            <ErrorBoundary>
              <NotificationSettings notification={notification} sendDate={sendDate} setSendDate={setSendDate} setType={setType} />
            </ErrorBoundary>
            <ErrorBoundary>
              <NotificationUserSegment articles={articles} methods={methods} notification={notification} t={t} />
            </ErrorBoundary>
            <ErrorBoundary>
              <NotificationContent
                articles={articles}
                content={content}
                methods={methods}
                news={news}
                notification={notification}
                setArticle={setArticle}
                setContent={setContent}
                type={type}
              />
            </ErrorBoundary>
            <Row className="margin-top-200">
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={loading} loading={loading} className="margin-right-100">
                    Save & schedule
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </MainPage>
  );
};
